import { Component, computed, effect, inject, input, signal } from '@angular/core';
import { rxResource, toObservable, toSignal } from '@angular/core/rxjs-interop';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { KENDO_LOADER } from '@progress/kendo-angular-indicators';
import { KENDO_CARD } from '@progress/kendo-angular-layout';
import { KENDO_SCHEDULER } from '@progress/kendo-angular-scheduler';
import { AbstractNewComponent } from '@unity/abstract/abstract-new.component';
import {
  AgreementsClient,
  CreatedResponseDto,
  FormScheduleCreateDto,
  FormSchedulesClient,
  FormTemplateClient,
  ScheduleTypesClient,
  UnityScheduleType,
} from '@unity/clients';
import { PageContainerComponent } from '@unity/components/layouts/page-container/page-container.component';
import { DropdownFormSchemasControlComponent } from '@unity/forms/dropdown-form-schemas-control/dropdown-form-schemas-control.component';
import { RadioConfirmationControlComponent } from '@unity/forms/radio-confirmation-control/radio-confirmation-control.component';
import { SelectorDateControlComponent } from '@unity/forms/selector-date-control/selector-date-control.component';
import { SelectorRecurrenceControlComponent } from '@unity/forms/selector-recurrence-control/selector-recurrence-control.component';
import { TextareaControlComponent } from '@unity/forms/textarea-control/textarea-control.component';
import { TextboxControlComponent } from '@unity/forms/textbox-control/textbox-control.component';
import { TextboxNumericIntegerControlComponent } from '@unity/forms/textbox-numeric-integer-control/textbox-numeric-integer-control.component';
import { ActionSharedComponent } from '@unity/shared/composed/action-shared/action-shared.component';
import { CardNewSharedComponent } from '@unity/shared/composed/card-new-shared/card-new-shared.component';
import {
  booleanFormControl,
  dateFormControl,
  nonZeroFormControl,
  numericFormControl,
  optionalStringFormControl,
  stringFormControl,
} from '@unity/utils/form-controls';
import { QuickActions } from '@unity/utils/quick-actions';
import { FormGroupControlSchema, UnityRouteUrlTrees } from '@unity/utils/types';
import { switchMap } from 'rxjs';

class FormScheduleCreateSchema implements FormGroupControlSchema<FormScheduleCreateDto> {
  public agreement_id: FormControl<number>;

  public type_id = nonZeroFormControl();
  public days_visible_before_open = numericFormControl({ validators: [Validators.min(0)] });
  public start_date = dateFormControl();
  public recurrence_rule = optionalStringFormControl();
  public name = stringFormControl();
  public description = stringFormControl();
  public form_schema_id = nonZeroFormControl();
  public is_one_time = booleanFormControl();
  public is_recurring = booleanFormControl();

  public constructor(agreement_id: number) {
    this.agreement_id = nonZeroFormControl({ value: agreement_id });
  }
}

@Component({
  selector: 'unity-new-form-schedule-page',
  standalone: true,
  imports: [
    KENDO_CARD,
    KENDO_LOADER,
    KENDO_SCHEDULER,
    ReactiveFormsModule,
    TextboxNumericIntegerControlComponent,
    TextboxControlComponent,
    TextareaControlComponent,
    SelectorDateControlComponent,
    SelectorRecurrenceControlComponent,
    RadioConfirmationControlComponent,
    PageContainerComponent,
    CardNewSharedComponent,
    ActionSharedComponent,
    DropdownFormSchemasControlComponent,
  ],
  templateUrl: './new-form-schedule-page.component.html',
  styleUrl: './new-form-schedule-page.component.scss',
})
export class NewFormSchedulePageComponent extends AbstractNewComponent<
  FormScheduleCreateDto,
  FormScheduleCreateSchema,
  CreatedResponseDto
> {
  /* Dependencies */
  private readonly agreementsClient = inject(AgreementsClient);
  private readonly formSchedulesClient = inject(FormSchedulesClient);
  private readonly formSchemasClient = inject(FormTemplateClient);
  private readonly scheduleTypesClient = inject(ScheduleTypesClient);

  /* Inputs */
  public readonly $agreementId = input.required({ alias: 'agreementId', transform: (value: string) => parseInt(value) });

  /* Impl */
  protected readonly $returnToAgreementAction = computed(() => QuickActions.ReturnToAgreement(this.$agreementId()));

  protected override readonly $contentHeader = signal('Create New Form Template');

  protected override createResourceFn$ = (form: FormGroup<FormScheduleCreateSchema>) => {
    const values = form.getRawValue();
    return this.formSchedulesClient.createOneForAgreement(values.agreement_id, values);
  };

  protected override redirectUrlFn = (response: CreatedResponseDto): UnityRouteUrlTrees => {
    const agreementId = this.$agreementId();
    return ['/agreement', agreementId, 'form', response.id];
  };

  protected override newControlsFn() {
    const agreementId = this.$agreementId();

    if (!agreementId) {
      return new FormScheduleCreateSchema(0);
    }

    return new FormScheduleCreateSchema(agreementId);
  }

  protected scheduleTypesResource = rxResource({
    stream: () => this.scheduleTypesClient.getAllAsMini(),
  });

  protected agreementResource = rxResource({
    params: () => {
      const agreementId = this.$agreementId();
      return agreementId ? { agreementId } : undefined;
    },
    stream: ({ params: { agreementId } }) => this.agreementsClient.getOneAsMini(agreementId),
  });

  protected formSchemasResource = rxResource({
    stream: () => this.formSchemasClient.getAllAsMini(),
  });

  protected readonly $formSchemas = computed(() => this.formSchemasResource.value()?.data ?? []);

  protected readonly $scheduleTypes = computed(() => this.scheduleTypesResource.value()?.data ?? []);

  private readonly $typeIdControlValue = toSignal(toObservable(this.$formGroup).pipe(switchMap(x => x.controls.type_id.valueChanges)));

  protected readonly $scheduleType = computed(() => {
    const scheduleTypes = this.$scheduleTypes();
    const typeId = this.$typeIdControlValue();
    return scheduleTypes?.find(t => t.id === typeId);
  });

  protected readonly $hasScheduleType = computed(() => {
    const type = this.$scheduleType();
    return !!type;
  });

  private readonly $isOneTimeControlValue = toSignal(
    toObservable(this.$formGroup).pipe(switchMap(x => x.controls.is_one_time.valueChanges))
  );

  private readonly $isRecurringControlValue = toSignal(
    toObservable(this.$formGroup).pipe(switchMap(x => x.controls.is_recurring.valueChanges))
  );

  protected readonly $agreementResourceLoading = computed(() => this.agreementResource.isLoading());

  protected readonly $contentHeaderSecondary = computed(() => {
    const agreement = this.agreementResource.value();
    return `Agreement: ${agreement?.name}`;
  });

  protected readonly $showRecurringQuestion = computed(() => {
    const isOneTimeControlValue = this.$isOneTimeControlValue();
    return isOneTimeControlValue == false;
  });

  protected readonly $showStartDate = this.$hasScheduleType;

  protected readonly $showDaysVisibleBeforeOpen = computed(() => {
    const type = this.$scheduleType();
    return type?.enum_value == UnityScheduleType.Recurring || type?.enum_value == UnityScheduleType.OneTime;
  });

  protected readonly $showFormCreator = this.$hasScheduleType;

  protected readonly $showScheduler = computed(() => {
    const type = this.$scheduleType();
    return type?.enum_value == UnityScheduleType.Recurring;
  });

  protected readonly $startDateLabel = computed(() => {
    const type = this.$scheduleType();
    switch (type?.enum_value) {
      case UnityScheduleType.Adhoc:
        return 'When should the form open?';
      case UnityScheduleType.OneTime:
        return "What's the due date for this form?";
      case UnityScheduleType.Recurring:
        return "What's the due date for the first recurring form?";
      default:
        return 'Start Date';
    }
  });

  protected readonly $startDateDescription = computed(() => {
    const type = this.$scheduleType();
    switch (type?.enum_value) {
      case UnityScheduleType.Adhoc:
        return 'Select the date when users will first be able to access the form.';
      case UnityScheduleType.OneTime:
        return 'This is the last day users can complete and submit the form.';
      case UnityScheduleType.Recurring:
        return 'Specifies when the first form is due. All future occurrences will follow the selected schedule starting from this date.';
      default:
        return 'Select the start date.';
    }
  });

  public constructor() {
    super();

    effect(() => {
      const isOneTimeControlValue = this.$isOneTimeControlValue();
      const scheduleTypes = this.$scheduleTypes();
      const formGroup = this.$formGroup();

      if (!scheduleTypes || !formGroup) {
        return;
      }

      formGroup.controls.start_date.reset();
      formGroup.controls.days_visible_before_open.reset();
      formGroup.controls.recurrence_rule.reset();

      if (isOneTimeControlValue === true) {
        const typeId = scheduleTypes.find(x => x.enum_value === UnityScheduleType.OneTime)?.id;
        if (typeId) {
          formGroup.controls.type_id.patchValue(typeId);
        }
        formGroup.controls.is_recurring.patchValue(false, { emitEvent: false });
      } else {
        formGroup.controls.is_recurring.reset();
      }
    });

    effect(() => {
      const isRecurringControlValue = this.$isRecurringControlValue();
      const scheduleTypes = this.$scheduleTypes();
      const formGroup = this.$formGroup();

      if (!scheduleTypes || !formGroup) {
        return;
      }

      formGroup.controls.start_date.reset();
      formGroup.controls.days_visible_before_open.reset();
      formGroup.controls.recurrence_rule.reset();

      if (isRecurringControlValue === true) {
        const typeId = scheduleTypes.find(x => x.enum_value === UnityScheduleType.Recurring)?.id;
        if (typeId) {
          formGroup.controls.type_id.patchValue(typeId);
        }
      } else if (isRecurringControlValue === false) {
        const typeId = scheduleTypes.find(x => x.enum_value === UnityScheduleType.Adhoc)?.id;
        if (typeId) {
          formGroup.controls.type_id.patchValue(typeId);
        }
      }
    });
  }
}
